import * as _ from 'lodash'
import * as React from 'react'
import { Alert, Button, Card, Input } from 'reactstrap';
import { ContentCardHeader } from '../../../components/ui/ContentCardHeader';
import { DataGrid, DataGridSortOrder } from '../../../components/ui/DataGrid';
import { FontAwesomeIcon } from '../../../components/ui/FontAwesomeIcon';
import { Tooltip } from '../../../components/ui/Tooltip';
import { archiveCommentCode, getCommentCodes, ICommentCodeIndex } from '../../../containers/CommentDetails/lib/http';
import { CommentCodeEditor } from './CommentCodeEditor';

interface ICommentCodesGridProps {
  isGlobal?: boolean
  onlyArchived?: boolean;
}

interface ICommentCodesGridState {
  commentCodes: ICommentCodeIndex[]
  filteredCommentCodes: ICommentCodeIndex[]
  commentCodeInEditMode: ICommentCodeIndex | null
  sortBy: keyof ICommentCodeIndex
  sortOrder: DataGridSortOrder
  loading: boolean
  commentCodeInDeleteMode: ICommentCodeIndex | null
  searchStr: string;
}

export class CommentCodesGrid extends React.Component<ICommentCodesGridProps, ICommentCodesGridState> {
  state: ICommentCodesGridState = {
    commentCodes: [],
    filteredCommentCodes: [],
    sortBy: 'id',
    sortOrder: 'asc',
    loading: false,
    commentCodeInEditMode: null,
    commentCodeInDeleteMode: null,
    searchStr: '',
  }

  setFilteredCodesDebounce = _.debounce(() => this.setFilteredCodes(), 1500)

  componentWillMount() {
    this.loadCommentCodes()
  }

  loadCommentCodes() {
    const { onlyArchived } = this.props

    this.setState({ loading: true, commentCodes: [], commentCodeInEditMode: null })

    getCommentCodes(false, onlyArchived).then((commentCodes) => {
      if (onlyArchived) {
        commentCodes = commentCodes.filter((cc) => cc.isArchived);
      }
      this.setState({ commentCodes, filteredCommentCodes: commentCodes, loading: false })
    })
  }

  setFilteredCodes() {
    const { commentCodes, searchStr } = this.state
    let filteredCommentCodes: ICommentCodeIndex[] = commentCodes

    if (searchStr) {
      const searchStrLower = searchStr.toLowerCase()

      filteredCommentCodes = commentCodes.filter((code) => {
        return Object.keys(code).some((codeKey) => {
          const valueStrLower = String(code[codeKey]).toLowerCase()

          return valueStrLower.indexOf(searchStrLower) >= 0;
        })
      })
    }

    this.setState({ filteredCommentCodes })
  }

  setSearchStr(searchStr: string) {
    this.setState({ searchStr }, () => {
      this.setFilteredCodesDebounce()
    })
  }

  updateCommentCodeInEditMode(commentCodeInEditMode: ICommentCodeIndex | null) {
    this.setState({ commentCodeInEditMode })
  }

  updateCommentCodeInDeleteMode(commentCodeInDeleteMode: ICommentCodeIndex | null) {
    this.setState({ commentCodeInDeleteMode });
  }

  archiveCommentCode(commentCode: ICommentCodeIndex) {
    this.setState({ loading: true })

    archiveCommentCode(commentCode)
      .then(() => this.loadCommentCodes())
  }

  render() {
    const { isGlobal = false, onlyArchived = false } = this.props
    const { commentCodeInEditMode, loading, commentCodeInDeleteMode } = this.state
    const commentCodeInEditModeId = commentCodeInEditMode ? commentCodeInEditMode.id : -1
    return (
      <Card>
        <ContentCardHeader
          sticky
          borderBottom
          header={
            <section className="d-md-flex justify-content-between">
              <header>
                <h2 className="d-inline-block mr-2">
                  {isGlobal &&
                    <span>Global</span>
                  }
                  {onlyArchived &&
                    <span>&nbsp;&nbsp;Archived</span>
                  }
                  <span>&nbsp;Comment Codes</span>
                  {loading &&
                    <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin muted />
                  }
                </h2>
              </header>
              <article>
                <Input type="text"
                  placeholder="Search"
                  onChange={(e) => this.setSearchStr(e.currentTarget.value)}
                  value={this.state.searchStr}
                />
              </article>
            </section>
          }
        />

        <DataGrid<ICommentCodeIndex>
          records={this.state.filteredCommentCodes}
          sortBy={this.state.sortBy}
          sortOrder={this.state.sortOrder}
          onSortChange={(sortBy, sortOrder) => this.setState({ sortBy, sortOrder })}
          showSubComponent={(record) => commentCodeInEditModeId === record.id}
          subComponent={(record) =>
            <CommentCodeEditor
              editMode={record}
              onSuccess={() => this.loadCommentCodes()}
              onCancel={() => this.updateCommentCodeInEditMode(null)} />
          }
          columns={[
            { property: 'id', header: 'ID' },
            { property: 'shortLabel', header: 'Label' },
            { property: 'longLabel', header: 'Description' },
            { property: 'typeLabel', header: 'Comment Code Type' },
            { property: 'mood', header: 'Mood' },
            {
              header: '', render: (record, isSubComponentShown) => {
                const isCommentCodeInDeleteMode = commentCodeInDeleteMode && commentCodeInDeleteMode.id === record.id

                return (
                  <section className="d-flex justify-content-end">
                    <Button color="white" className="ml-2" disabled={isSubComponentShown || loading}
                      onClick={() => this.updateCommentCodeInEditMode(record)}>
                      <Tooltip text="Edit">
                        <FontAwesomeIcon icon="pencil-alt" fixedWidth size="sm" />
                      </Tooltip>
                    </Button>
                    {!onlyArchived &&
                      <Button color="white" className="ml-2" disabled={isSubComponentShown || loading}
                        onClick={() => this.updateCommentCodeInDeleteMode(record)}>
                        <Tooltip text="Archive">
                          <FontAwesomeIcon icon="archive" fixedWidth size="sm" />
                        </Tooltip>
                      </Button>
                    }
                    {isCommentCodeInDeleteMode &&
                      <Alert color="warning" className="table-warning rounded-0" style={{ position: 'absolute', left: 0, right: 0, height: 66, marginTop: -15 }}>
                        <section className="d-flex justify-content-end">
                          <article>
                            <p className="text-bold m-0" style={{ fontSize: '1.1rem' }}>Once archived a comment code is unavailable for selection.
                              <br />Would you like to archive the comment code: {record.shortLabel}?</p>
                          </article>
                          <article className="text-right" style={{ minWidth: 250 }}>
                            <Button color="light" className="mr-2"
                              onClick={() => this.updateCommentCodeInDeleteMode(null)}>
                              <span>Cancel</span>
                            </Button>
                            <Button color="action"
                              onClick={() => this.archiveCommentCode(record)}>
                              <FontAwesomeIcon icon="archive" fixedWidth size="sm" />
                              <span>&nbsp;Yes, Archive</span>
                            </Button>
                          </article>
                        </section>
                      </Alert>
                    }
                  </section>
                )
              }
            }
          ]}
        />
      </Card>
    )
  }
}
